import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, Loader2, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { LoginFormData } from '../../../types/auth';
import { validationRules } from '../../../utils/validation';
import { authService } from '../../../services/authService';

interface LoginFormProps {
  onSuccess?: () => void;
}

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setApiError(null);

      const response = await authService.login(data.email, data.password);

      // Store tokens and user data
      sessionStorage.setItem('authToken', response.data.accessToken);
      sessionStorage.setItem('refreshToken', response.data.refreshToken);
      sessionStorage.setItem('user', JSON.stringify(response.data.user));

      reset();
      navigate('/dashboard');
      onSuccess?.();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setApiError('Invalid email or password');
        } else if (error.code === 'ERR_NETWORK') {
          setApiError('Unable to connect. Please check your connection.');
        } else if (error.code === 'ECONNABORTED') {
          setApiError('Request timed out. Please try again.');
        } else if (error.response?.status && error.response.status >= 500) {
          setApiError('Server error. Please try again later.');
        } else {
          setApiError('An error occurred. Please try again.');
        }
      } else if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* API Error Display */}
      {apiError && (
        <div
          role="alert"
          aria-live="polite"
          className="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg text-red-400 text-sm"
        >
          {apiError}
        </div>
      )}

      {/* Email Input */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
          EMAIL ADDRESS
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            id="email"
            type="email"
            {...register('email', validationRules.email)}
            className={`w-full pl-10 pr-4 py-3 bg-gray-800 border ${
              errors.email ? 'border-red-500' : 'border-gray-700'
            } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
            style={{ '--tw-ring-color': '#23F47C' } as React.CSSProperties}
            placeholder="admin@gymnet.com"
            autoComplete="email"
            aria-label="Email address"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
        </div>
        {errors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-400">
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password Input */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="password" className="block text-sm font-medium text-gray-300">
            PASSWORD
          </label>
          <Link
            to="/forgot-password"
            className="text-sm text-gray-400 transition-colors"
            style={{ color: '#9ca3af' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#23F47C')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#9ca3af')}
          >
            Forgot Password?
          </Link>
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            {...register('password', validationRules.password)}
            className={`w-full pl-10 pr-12 py-3 bg-gray-800 border ${
              errors.password ? 'border-red-500' : 'border-gray-700'
            } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
            style={{ '--tw-ring-color': '#23F47C' } as React.CSSProperties}
            placeholder="••••••••"
            autoComplete="current-password"
            aria-label="Password"
            aria-invalid={errors.password ? 'true' : 'false'}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.password && (
          <p id="password-error" className="mt-1 text-sm text-red-400">
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 text-black"
        style={{ backgroundColor: '#23F47C' }}
        onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#1de06d')}
        onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#23F47C')}
        aria-label="Sign in to admin portal"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Signing in...</span>
          </>
        ) : (
          <>
            <span>Sign in to Portal</span>
            <ArrowRight className="h-5 w-5" />
          </>
        )}
      </button>
    </form>
  );
};
